import { useCallback } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useBalance } from '@hooks/api/useBalance';
import { useUnlockAdditionalContacts } from '@hooks/common/useUnlockAdditionalContacts';
import { get } from 'lodash-es';

import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import { Avatar, Box, Chip, Divider, Drawer, IconButton, Rating, Typography } from '@mui/material';
import { AdditionalContacts } from '@components/AdditionalContacts';

import { useTheme as useAppTheme } from '@/components/theme-v2/ThemeContext';
import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

interface FullDetailsDrawerProps {
  row: {
    data?: Record<string, any>;
    raw?: Record<string, any>;
    purchased?: boolean;
    is_purchased?: boolean;
    _isPreviewRow?: boolean;
    _rowIndex?: number;
    [key: string]: any;
  };
}

// Helper to check if contact data should be blurred
const shouldBlurContactData = (row: FullDetailsDrawerProps['row']): boolean => {
  const isPreviewRow = row._isPreviewRow === true;
  const isPurchased = row.purchased === true || row.is_purchased === true;
  return !isPreviewRow && !isPurchased;
};

const InfoRow = ({
  icon,
  label,
  value,
  color = '#00d4aa',
  copyable = false,
  isDark,
  blurred = false,
  blurPlaceholder,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | undefined | null;
  color?: string;
  copyable?: boolean;
  isDark: boolean;
  blurred?: boolean;
  blurPlaceholder?: string;
}) => {
  const displayValue = blurred ? blurPlaceholder || value || 'N/A' : value || 'N/A';
  const hasValue = value && value !== 'N/A';

  const handleCopy = useCallback(() => {
    if (hasValue && !blurred) {
      navigator.clipboard.writeText(value);
    }
  }, [value, hasValue, blurred]);

  // Pre-compute colors based on isDark prop (fixes NiceModal theme context issue)
  const labelColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)';
  const valueColor = hasValue
    ? isDark
      ? '#ffffff'
      : '#1a1f2e'
    : isDark
      ? 'rgba(255,255,255,0.5)'
      : 'rgba(0,0,0,0.38)';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const iconBtnColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.38)';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        '&:hover': {
          backgroundColor: hoverBg,
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          backgroundColor: blurred ? 'rgba(128, 128, 128, 0.2)' : `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: labelColor,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: blurred ? '#666' : valueColor,
            wordBreak: 'break-word',
            fontWeight: hasValue ? 500 : 400,
            filter: blurred ? 'blur(5px)' : 'none',
            userSelect: blurred ? 'none' : 'auto',
            transition: 'filter 0.2s ease',
          }}
        >
          {displayValue}
        </Typography>
        {blurred && (
          <Typography
            variant="caption"
            sx={{
              color: '#ffb74d',
              fontSize: '0.65rem',
              display: 'block',
              mt: 0.25,
            }}
          >
            Purchase to reveal
          </Typography>
        )}
      </Box>
      {copyable && hasValue && !blurred && (
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            color: iconBtnColor,
            '&:hover': { color: '#00d4aa' },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <Box sx={{ px: 2, pt: 3, pb: 1 }}>
    <Typography
      variant="overline"
      sx={{
        color: '#00d4aa',
        fontWeight: 600,
        letterSpacing: '1px',
        fontSize: '0.75rem',
      }}
    >
      {title}
    </Typography>
  </Box>
);

const FullDetailsDrawer = NiceModal.create<FullDetailsDrawerProps>(({ row }) => {
  const modal = useModal();
  const { theme: appTheme } = useAppTheme(); // Use custom theme context
  const { isLargerScreen } = useScreenSizeContext();
  const { unlockSingleLead } = useUnlockAdditionalContacts();
  const { data: balanceData } = useBalance();

  // Get user's current credit balance
  const userCredits = balanceData?.token ?? balanceData?.balance ?? 0;

  // Detect theme mode from app's custom ThemeContext
  const isDark = appTheme === 'dark';

  const handleClose = () => {
    modal.resolve(true);
    modal.remove();
  };

  // Extract data from various possible locations
  // Priority: top-level flattened fields first, then nested data, then raw
  const getData = (key: string): any => {
    const topLevel = get(row, key);
    const fromData = get(row, `data.${key}`);
    const fromRaw = get(row, `raw.${key}`);

    // Return first non-empty value
    if (topLevel !== undefined && topLevel !== null && topLevel !== '') return topLevel;
    if (fromData !== undefined && fromData !== null && fromData !== '') return fromData;
    if (fromRaw !== undefined && fromRaw !== null && fromRaw !== '') return fromRaw;
    return '';
  };

  // Helper to clean values (handle NaN, undefined, null, objects)
  const cleanValue = (value: any): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object' && !Array.isArray(value)) return '';
    if (typeof value === 'number' && Number.isNaN(value)) return '';
    if (value === 'NaN' || value === 'undefined' || value === 'null') return '';
    return String(value);
  };

  const name = cleanValue(getData('name'));
  const phone = cleanValue(getData('phone'));
  const emailRaw = getData('email');
  const email = Array.isArray(emailRaw) ? emailRaw[0] : cleanValue(emailRaw);
  const street = cleanValue(getData('street')) || cleanValue(getData('address'));
  const city = cleanValue(getData('city'));
  const state = cleanValue(getData('state'));
  const zipCode = cleanValue(getData('zip_code')) || cleanValue(getData('zip')) || cleanValue(getData('postal_code'));
  const website = cleanValue(getData('website')) || cleanValue(getData('url'));
  const source = cleanValue(getData('source'));
  const sourceUrl = cleanValue(getData('source_url'));
  const rating = getData('rating');
  const categories = getData('categories');
  const campaignName = cleanValue(getData('campaign_name'));
  const scraperName = cleanValue(getData('scraper_name'));
  const createdAt = cleanValue(getData('created_at'));
  const scrapedAt = cleanValue(getData('scraped_at'));
  const company = cleanValue(getData('company')) || name; // Fallback to name for company
  const positionRaw = getData('position');
  const position = cleanValue(positionRaw) && positionRaw !== 'NaN' ? cleanValue(positionRaw) : '';
  const linkedinUrl = cleanValue(getData('linkedin_url'));
  const reviewCount = cleanValue(getData('review_count')) || cleanValue(getData('reviews'));

  // Extract additional contacts from additionalData or directly from data
  const additionalData = row.additionalData || row.additional_data || row.data?.additionalData || {};
  const rawAdditionalContacts = additionalData.additional_contacts || row.data?.additional_contacts || [];

  // Transform API format {type, value} to component format {email, phone, name}
  // API returns: [{type: "phone", value: "+1..."}, {type: "email", value: "...@..."}]
  // Component expects: [{email: "...", phone: "...", name: "...", title: "..."}]
  const additionalContacts = rawAdditionalContacts.map((contact: any) => {
    if (contact.type && contact.value) {
      // API format: {type, value}
      return {
        name: contact.name || '',
        email: contact.type === 'email' ? contact.value : '',
        phone: contact.type === 'phone' ? contact.value : '',
        title: contact.title || '',
      };
    }
    // Already in component format
    return contact;
  });

  const additionalEmails: string[] = additionalData.additional_emails || row.data?.additional_emails || [];
  const additionalPhones: string[] = additionalData.additional_phones || row.data?.additional_phones || [];
  const additionalContactsUnlocked =
    additionalData.additional_contacts_unlocked || row.data?.additional_contacts_unlocked || false;
  const leadId = cleanValue(getData('id')) || cleanValue(getData('leads_id')) || '';

  // Build full address from components
  const addressParts = [street, city, state, zipCode].filter(Boolean);
  const fullAddress = addressParts.join(', ') || '';

  // Determine if contact data should be blurred
  const shouldBlur = shouldBlurContactData(row);

  // Parse rating - ensure it's a valid number
  const ratingValue = typeof rating === 'string' ? parseFloat(rating) : typeof rating === 'number' ? rating : NaN;
  const numRating = !Number.isNaN(ratingValue) && Number.isFinite(ratingValue) ? ratingValue : 0;
  const hasRating = numRating > 0;

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // Get initials for avatar
  const getInitials = (displayName: string) => {
    if (!displayName) return '?';
    return displayName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Parse categories
  const categoryList = Array.isArray(categories) ? categories : categories ? [categories] : [];

  return (
    <Drawer
      key={`drawer-${appTheme}`} // Force re-render on theme change
      anchor="right"
      open={modal.visible}
      onClose={handleClose}
      variant="persistent" // Use persistent drawer so it doesn't block interactions
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          width: isLargerScreen ? 420 : 360,
          backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
          backgroundImage: 'none',
          top: '64px', // Position below header
          height: 'calc(100% - 64px)', // Adjust height
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #00d4aa 0%, #00a884 100%)',
          p: 3,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: 'rgba(255,255,255,0.2)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'white',
            }}
          >
            {getInitials(name)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
              {name || 'Unknown Business'}
            </Typography>
            {hasRating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Rating
                  value={numRating}
                  precision={0.5}
                  size="small"
                  readOnly
                  sx={{ '& .MuiRating-iconFilled': { color: '#fff' } }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  ({numRating.toFixed(1)})
                </Typography>
              </Box>
            )}
            {categoryList.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {categoryList.slice(0, 2).map(cat => (
                  <Chip
                    key={`cat-${String(cat)}`}
                    label={cat}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', pb: 3 }}>
        {/* Contact Information - Only show fields that have data */}
        <SectionHeader title="Contact Information" />
        {/* Only show phone if it has data (or masked data for unpurchased) */}
        {phone && (
          <InfoRow
            icon={<PhoneIcon sx={{ color: shouldBlur ? '#666' : '#4fc3f7', fontSize: 20 }} />}
            label="Phone"
            value={phone}
            color="#4fc3f7"
            copyable
            isDark={isDark}
            blurred={shouldBlur}
          />
        )}
        {/* Only show email if it has data */}
        {email && (
          <InfoRow
            icon={<EmailIcon sx={{ color: shouldBlur ? '#666' : '#ba68c8', fontSize: 20 }} />}
            label="Email"
            value={email}
            color="#ba68c8"
            copyable
            isDark={isDark}
            blurred={shouldBlur}
          />
        )}
        {/* Only show address if it has data */}
        {fullAddress && (
          <InfoRow
            icon={<LocationOnIcon sx={{ color: shouldBlur ? '#666' : '#ef5350', fontSize: 20 }} />}
            label="Address"
            value={fullAddress}
            color="#ef5350"
            copyable
            isDark={isDark}
            blurred={shouldBlur}
          />
        )}
        {/* Only show website if it has data */}
        {website && (
          <InfoRow
            icon={<LanguageIcon sx={{ color: shouldBlur ? '#666' : '#90caf9', fontSize: 20 }} />}
            label="Website"
            value={website}
            color="#90caf9"
            copyable
            isDark={isDark}
            blurred={shouldBlur}
          />
        )}
        {/* Only show LinkedIn if it has data */}
        {linkedinUrl && (
          <InfoRow
            icon={<LinkIcon sx={{ color: shouldBlur ? '#666' : '#0077b5', fontSize: 20 }} />}
            label="LinkedIn"
            value={linkedinUrl}
            color="#0077b5"
            copyable
            isDark={isDark}
            blurred={shouldBlur}
          />
        )}
        {/* Show message if no contact info available */}
        {!phone && !email && !fullAddress && !website && !linkedinUrl && (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontStyle: 'italic' }}
            >
              No contact information available for this lead.
            </Typography>
          </Box>
        )}

        {/* Additional Contacts Section */}
        {(additionalContacts.length > 0 || additionalEmails.length > 0 || additionalPhones.length > 0) && (
          <Box sx={{ px: 2 }}>
            <AdditionalContacts
              leadId={leadId}
              additionalContacts={additionalContacts}
              additionalEmails={additionalEmails}
              additionalPhones={additionalPhones}
              isUnlocked={additionalContactsUnlocked}
              onUnlock={unlockSingleLead}
              userCredits={userCredits}
            />
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} />

        {/* Business Details */}
        <SectionHeader title="Business Details" />
        <InfoRow
          icon={<BusinessIcon sx={{ color: '#00d4aa', fontSize: 20 }} />}
          label="Business Name"
          value={name}
          color="#00d4aa"
          isDark={isDark}
        />
        {company && company !== name && (
          <InfoRow
            icon={<BusinessIcon sx={{ color: '#ffb74d', fontSize: 20 }} />}
            label="Company"
            value={company}
            color="#ffb74d"
            isDark={isDark}
          />
        )}
        {position && position.length > 0 && (
          <InfoRow
            icon={<BusinessIcon sx={{ color: '#81c784', fontSize: 20 }} />}
            label="Position"
            value={position}
            color="#81c784"
            isDark={isDark}
          />
        )}
        {hasRating && (
          <InfoRow
            icon={<StarIcon sx={{ color: '#ffd700', fontSize: 20 }} />}
            label="Rating"
            value={
              reviewCount ? `${numRating.toFixed(1)} / 5.0 (${reviewCount} reviews)` : `${numRating.toFixed(1)} / 5.0`
            }
            color="#ffd700"
            isDark={isDark}
          />
        )}
        {categoryList.length > 0 && (
          <InfoRow
            icon={<CategoryIcon sx={{ color: '#ffb74d', fontSize: 20 }} />}
            label="Categories"
            value={categoryList.join(', ')}
            color="#ffb74d"
            isDark={isDark}
          />
        )}

        <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} />

        {/* Source Information */}
        <SectionHeader title="Source Information" />
        <InfoRow
          icon={<LanguageIcon sx={{ color: '#90caf9', fontSize: 20 }} />}
          label="Source"
          value={source || scraperName}
          color="#90caf9"
          isDark={isDark}
        />
        {sourceUrl && (
          <InfoRow
            icon={<LinkIcon sx={{ color: '#ce93d8', fontSize: 20 }} />}
            label="Source URL"
            value={sourceUrl}
            color="#ce93d8"
            copyable
            isDark={isDark}
          />
        )}
        <InfoRow
          icon={<CampaignIcon sx={{ color: '#4db6ac', fontSize: 20 }} />}
          label="Campaign"
          value={campaignName}
          color="#4db6ac"
          isDark={isDark}
        />
        <InfoRow
          icon={<CalendarTodayIcon sx={{ color: '#aed581', fontSize: 20 }} />}
          label="Scraped At"
          value={formatDate(scrapedAt || createdAt)}
          color="#aed581"
          isDark={isDark}
        />
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` }}>
        <Box
          component="button"
          onClick={handleClose}
          sx={{
            width: '100%',
            py: 1.5,
            backgroundColor: '#00d4aa',
            color: '#1a1f2e',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: '#00e5b8',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Drawer>
  );
});

export const FullDetailsModalId = 'FullDetailsModal';
NiceModal.register('FullDetailsModal', FullDetailsDrawer);
